import { isValidElement } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'

import { BaseViewFieldTemplate } from '@/Ship/Templates/Form/BaseViewFieldTemplate'

import { LETTER_EXTENSIONS } from './letterExtensions'
import { TABLE_EXTENSIONS } from './tableExtensions'

import { isRichTextEmpty } from './richTextUtils'
import './rich-text.css'

import type { FC, ReactNode } from 'react'

/** Ship-шаблон — .jsx без типів; children у пропсах відновлюємо аліасом. */
const ViewFieldTemplate = BaseViewFieldTemplate as FC<{ field: unknown; children?: ReactNode }>

interface RichTextViewProps {
    html?: string | null
}

/**
 * RichTextView — БЕЗПЕЧНИЙ read-only показ HTML опису (SPEC-008 §6.3, рев'ю 06).
 *
 * Рендеримо тим самим tiptap (`editable:false`, StarterKit): парсер відкидає невідомі
 * ядру вузли/атрибути — це і є санітизація БЕЗ нової залежності (НЕ dangerouslySetInnerHTML,
 * НЕ dompurify). Показ порожнього опису — «—».
 *
 * Колір тексту й маркер (пресет «letter», Q13.5) теж є в схемі показу
 * (`letterExtensions`), а також таблиці пресета «description» (Q2.8,
 * `tableExtensions`): без них таблиця мовчки зникла б із картки.
 */
export const RichTextView = ({ html }: RichTextViewProps) => {
    const value = typeof html === 'string' && !isRichTextEmpty(html) ? html : ''

    // Хук викликаємо завжди (правила hooks); deps=[value] — перестворюємо read-only
    // редактор при зміні значення (напр. після reload картки).
    const editor = useEditor(
        {
            extensions: [StarterKit, ...LETTER_EXTENSIONS, ...TABLE_EXTENSIONS],
            content: value,
            editable: false,
            immediatelyRender: true,
        },
        [value]
    )

    if (!value) return <span className="rich-text-view rich-text-view--empty">—</span>

    return (
        <div className="rich-text-view">
            <EditorContent editor={editor} />
        </div>
    )
}

/**
 * view-функція rich-text поля (наприклад, `description`).
 *
 * УРОК 06: ядро кличе view-функції ПОЗИЦІЙНО — `view(value, formContext)`
 * (`Core/UI/Form/Stores/BaseFieldStore.ts:481`, через `field.value`). Але view-гілка
 * `FormField` додатково ре-викликає той самий view з ОБ'ЄКТОМ — `view({field, formContext, value})`
 * (`FormField.tsx:380`), причому `value` там = вже відрендерений результат першого виклику.
 * Тому хендлер робимо ідемпотентним до обох конвенцій і до готового ReactNode.
 */
export const renderRichTextView = (arg1: unknown, _formContext?: unknown): ReactNode => {
    // Вже відрендерений елемент (повторний виклик FormField) — повертаємо як є.
    if (isValidElement(arg1)) return arg1

    let html: unknown = arg1
    let field: unknown
    if (arg1 !== null && typeof arg1 === 'object' && !Array.isArray(arg1) && 'value' in arg1) {
        html = (arg1 as Record<string, unknown>)['value']
        field = (arg1 as Record<string, unknown>)['field']
    }
    const content = isValidElement(html) ? html : <RichTextView html={typeof html === 'string' ? html : ''} />

    // Об'єктний виклик FormField передає `field` — загортаємо в Ship-шаблон
    // (той самий, що FormConfig.defaultViewFieldTemplate ставить усім view-полям),
    // щоб над значенням був лейбл «Опис» у єдиному стилі: FormField для полів із
    // кастомним `view` повертає результат view-функції БЕЗ шаблона (FormField.tsx:377),
    // тож лейбл — відповідальність самого view.
    if (field) {
        return <ViewFieldTemplate field={field}>{content}</ViewFieldTemplate>
    }

    return content
}
